import { useState, useEffect, useCallback } from 'react';
import { articleRepository } from '../../../../domain/repositories';

/**
 * Custom hook to manage articles data and pagination
 * @returns {Object} Articles state and handlers
 */
export const useArticles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [paginationInfo, setPaginationInfo] = useState(null);

  const loadArticles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [articlesData, pagination] = await Promise.all([
        currentPage === 0 
          ? articleRepository.getArticlesFirstPage()
          : articleRepository.getArticlesForPage(currentPage),
        articleRepository.getPaginationInfo(currentPage)
      ]);
      
      setArticles(articlesData);
      setPaginationInfo(pagination);
    } catch (err) {
      console.error('Error loading articles:', err);
      setError('Failed to load articles. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const handleNextPage = () => {
    if (paginationInfo?.hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo?.hasPreviousPage) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const retry = () => {
    loadArticles();
  };

  return {
    articles,
    loading,
    error,
    currentPage,
    paginationInfo,
    handleNextPage,
    handlePreviousPage,
    retry
  };
};
